import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from '../services/api.service';
import { StoredToken } from '../models/token';
import { AuthService } from '../services/auth.service';
import { StorageService } from '../services/storage.service';
import { Router } from '@angular/router';
import { ToastService } from '../_core/services/toast.service';
import { LocaleService } from '../services/locale.service';

const client = {
  origin: environment.token.clientOrigin,
  expected: environment.token.clientExpected,
} as const;

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor implements HttpInterceptor {
  private readonly toastService = inject(ToastService);
  private readonly localeService = inject(LocaleService);

  storedToken: StoredToken;

  constructor(
    private apiService: ApiService,
    private storageService: StorageService,
    private authService: AuthService,
    private readonly router: Router,
  ) { }

  private static addAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    if (req.url.startsWith('https://ingreso.ceibal.edu.uy')) {
      return req;
    }
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  }

  private static shouldLogout(err: HttpErrorResponse): boolean {
    if (['/config', '/login'].some((u) => err.url.endsWith(u)) || err.url.includes('login/external')) {
      return false;
    }

    return err.status === 401;
  }

  private static shouldAskForAnotherToken(err: HttpErrorResponse): boolean {
    return err.url.includes('/configuration');
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const endsWithList = ['/token'];
    const includesList = ['amazon', 'assets'];

    if (
      endsWithList.some((v) => req.url.endsWith(v)) ||
      includesList.some((v) => req.url.includes(v)) ||
      req.url.startsWith(environment.translationsBaseUrl)
    ) {
      return next.handle(req);
    }

    this.storedToken = this.resolveStoredToken();

    if (this.storedToken) {
      if (new Date().getTime() < this.storedToken.expires) {
        return next.handle(TokenInterceptor.addAuthHeader(req, this.storedToken.token.token)).pipe(
          catchError((err, _event) => {
            if (TokenInterceptor.shouldAskForAnotherToken(err) && this.storedToken) {
              this.storageService.remove('user');
              localStorage.removeItem('userId');
              delete this.storedToken;
              return this.getToken(req, next);
            } else if (TokenInterceptor.shouldLogout(err)) {
              const isAuth = this.authService.isAuthenticated() || localStorage.getItem('userId') !== null;

              delete this.storedToken;
              this.authService.localLogout();
              this.localeService.init().then(() => {
                this.toastService.showToast('messages.error.sessionExpired', 'warning');
              });
              this.router.navigate(['/']).then(() => {
                if (isAuth) {
                  (window as any).location.reload();
                }
              });
              return next.handle(req);
            }

            throw err;
          })
        );
      }
    }

    return this.getToken(req, next);
  }

  private resolveStoredToken(): StoredToken {
    if (this.storedToken) {
      return this.storedToken;
    }

    const tokenString = this.storageService.get('token');

    if (tokenString) {
      return JSON.parse(tokenString);
    }

    return null;
  }

  private getToken(req: HttpRequest<any>, next: HttpHandler) {
    return this.apiService.getToken(client.origin, client.expected).pipe(
      switchMap((newToken) => {
        const expiration = new Date().getTime() + newToken.expiresIn * 1000;

        this.storedToken = {
          expires: new Date(expiration).getTime(),
          token: newToken,
        };

        this.storageService.set('token', JSON.stringify(this.storedToken));
        req = TokenInterceptor.addAuthHeader(req, newToken.token);
        return next.handle(req);
      })
    );
  }
}
